/**
 * Phase Multi-Magasin — STEP 1 admin page.
 *
 * CRUD on magasins. Carlos can :
 *   - List active + archived
 *   - Create new (code auto-suggested M1, M2, M3, …)
 *   - Inline edit name / address / phone
 *   - Archive (soft, R1) / Restore
 *   - Mark a magasin as the default (one-click — backend exposes is_default)
 *
 * Limits : at most 99 magasins. Default magasin cannot be archived.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store, Plus, Edit2, Archive, RotateCcw, Save, X, Loader2, CheckCircle2,
  AlertTriangle, ArrowLeft, ShieldCheck,
} from 'lucide-react';
import Layout from '../components/Layout';
import AdminEquipeMagasinsTabs from '../components/AdminEquipeMagasinsTabs';
import EditMagasinModal from '../components/magasins/EditMagasinModal';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Status badge CSS classes only — the visible label is translated at render time.
const STATUS_BADGE = {
  active: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  archived: { cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
};

// 2026-05-19 — RowEditor inline replaced by EditMagasinModal (Carlos clarif §2).
// The modal now hosts symmetric ZPL/ESC-POS printer sections + per-printer Test.



const CreateModal = ({ open, onClose, onCreated, api, existingMagasins }) => {
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [printerIp, setPrinterIp] = useState('');
  const [color, setColor] = useState('#10b981');
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // C12.1 — preset palette + smart default color depending on magasin index.
  const PALETTE = ['#10b981', '#3b82f6', '#a855f7', '#f97316', '#ec4899', '#eab308', '#06b6d4', '#ef4444'];
  const currentDefaultMag = (existingMagasins || []).find((m) => m.is_default);

  useEffect(() => {
    if (!open) return;
    setName(''); setAddress(''); setPhone(''); setPrinterIp(''); setIsDefault(false); setError('');
    // Pick a default color that doesn't clash with existing magasins (best-effort).
    const used = new Set((existingMagasins || []).map((m) => (m.color || '').toLowerCase()));
    const nextColor = PALETTE.find((c) => !used.has(c.toLowerCase())) || PALETTE[0];
    setColor(nextColor);
    api.get('/magasins/next-code').then((r) => setCode(r.data?.code || 'M3')).catch(() => setCode('M3'));
  }, [open, api]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setError('');
    if (!name.trim()) { setError(t('adminMagasins.nameRequired')); return; }
    // Confirmation if user is overriding an existing default.
    if (isDefault && currentDefaultMag && currentDefaultMag.code !== code.toUpperCase()) {
      if (!window.confirm(
        t('adminMagasins.confirmOverrideDefault', { code: currentDefaultMag.code, name: currentDefaultMag.name }),
      )) return;
    }
    setBusy(true);
    try {
      const res = await api.post('/admin/magasins', {
        code: code.toUpperCase(),
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        printer_ip: printerIp.trim(),
        color,
      });
      // If user asked for is_default, PATCH it in a follow-up call (POST endpoint
      // ignores is_default by design — only super-admin patch toggles it).
      if (isDefault) {
        try {
          await api.patch(`/admin/magasins/${code.toUpperCase()}`, { is_default: true });
        } catch (e) {
          // Non-blocking — the magasin is created; flag the issue to the user.
          // eslint-disable-next-line no-console
          console.warn('is_default toggle failed', e);
        }
      }
      onCreated?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminMagasins.createError'));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="magasin-create-modal">
      <div className="w-full max-w-lg bg-gray-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-400" />
            <h2 className="text-base font-semibold">{t('adminMagasins.newStore')}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" data-testid="magasin-create-close"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldCode')}</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={5} className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white font-mono uppercase" data-testid="magasin-create-code" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldName')}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('adminMagasins.namePlaceholder')} className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white" data-testid="magasin-create-name" autoFocus />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldAddress')}</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white" data-testid="magasin-create-address" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldPhone')}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white" data-testid="magasin-create-phone" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldPrinterIp')}</label>
            <input
              value={printerIp}
              onChange={(e) => setPrinterIp(e.target.value)}
              placeholder={t('adminMagasins.printerIpPlaceholder')}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white font-mono text-sm"
              data-testid="magasin-create-printer-ip"
            />
            <p className="text-[10px] text-gray-500 mt-1">{t('adminMagasins.printerIpHint')}</p>
          </div>
          <div>
            <label className="text-[11px] uppercase text-gray-500">{t('adminMagasins.fieldBadgeColor')}</label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                data-testid="magasin-create-color-input"
              />
              <input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-24 px-2 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                data-testid="magasin-create-color-hex"
              />
              <div className="flex gap-1.5 flex-wrap" data-testid="magasin-create-color-palette">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${color.toLowerCase() === c.toLowerCase() ? 'border-white' : 'border-white/20'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md border" style={{ color, borderColor: `${color}60`, backgroundColor: `${color}1A` }}>
              <span className="w-3 h-3 rounded-full" style={{ background: color }} />
              <span className="text-xs font-bold uppercase">{(code || 'MX')}</span>
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="magasin-create-default-toggle">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 accent-amber-500"
              />
              <span className="text-sm text-gray-200">{t('adminMagasins.setAsDefault')}</span>
            </label>
            {isDefault && currentDefaultMag && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {t('adminMagasins.defaultWillBeDemoted', { code: currentDefaultMag.code, name: currentDefaultMag.name })}
                </span>
              </div>
            )}
          </div>
          {error && <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-lg p-2 text-xs" data-testid="magasin-create-error">{error}</div>}
        </div>
        <footer className="p-4 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm">{t('adminMagasins.cancel')}</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2" data-testid="magasin-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t('adminMagasins.createStore')}
          </button>
        </footer>
      </div>
    </div>
  );
};

const AdminMagasinsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [magasins, setMagasins] = useState([]);
  const [filter, setFilter] = useState('active'); // active | archived | all
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // magasin object being edited (or null)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchList = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.get('/magasins', { params: { active_only: false } });
      setMagasins(res.data?.magasins || []);
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminMagasins.loadError'));
    } finally { setBusy(false); }
  };

  useEffect(() => { fetchList(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filter === 'active') return magasins.filter((m) => m.is_active);
    if (filter === 'archived') return magasins.filter((m) => !m.is_active);
    return magasins;
  }, [magasins, filter]);

  const handleArchive = async (code) => {
    if (!window.confirm(t('adminMagasins.confirmArchive', { code }))) return;
    try {
      await api.post(`/magasins/${code}/archive`);
      fetchList();
    } catch (e) {
      window.alert(e?.response?.data?.detail || t('adminMagasins.genericError'));
    }
  };

  const handleRestore = async (code) => {
    try {
      await api.post(`/magasins/${code}/restore`);
      fetchList();
    } catch {}
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-magasins-page">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* C12.3 — Fusion visuelle Équipe & Magasins */}
        <AdminEquipeMagasinsTabs active="magasins" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate(-1)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3 h-3" /> {t('adminMagasins.back')}
            </button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Store className="w-6 h-6 text-emerald-400" /> {t('adminMagasins.title')}
            </h1>
            <p className="text-sm text-gray-400">{t('adminMagasins.subtitle')}</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 font-semibold text-sm flex items-center gap-2" data-testid="magasin-new-btn">
            <Plus className="w-4 h-4" /> {t('adminMagasins.newStore')}
          </button>
        </div>

        <div className="flex gap-2 mb-3">
          {[
            { id: 'active', label: t('adminMagasins.filterActive') },
            { id: 'archived', label: t('adminMagasins.filterArchived') },
            { id: 'all', label: t('adminMagasins.filterAll') },
          ].map((opt) => (
            <button key={opt.id} onClick={() => setFilter(opt.id)} className={`px-3 py-1.5 rounded-lg text-xs ${filter === opt.id ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'}`} data-testid={`magasin-filter-${opt.id}`}>
              {opt.label}
            </button>
          ))}
        </div>

        {error && <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-xl p-3 text-sm mb-3">{error}</div>}

        <div className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-gray-400 text-xs">
              <tr>
                <th className="px-3 py-2 text-left w-20">{t('adminMagasins.colCode')}</th>
                <th className="px-3 py-2 text-left">{t('adminMagasins.colName')}</th>
                <th className="px-3 py-2 text-left">{t('adminMagasins.colAddress')}</th>
                <th className="px-3 py-2 text-left w-32">{t('adminMagasins.colPhone')}</th>
                <th className="px-3 py-2 text-left w-36">{t('adminMagasins.colPrinterIp')}</th>
                <th className="px-3 py-2 text-left w-32">{t('adminMagasins.colColor')}</th>
                <th className="px-3 py-2 text-left w-24">{t('adminMagasins.colStatus')}</th>
                <th className="px-3 py-2 text-right w-44">{t('adminMagasins.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {busy && magasins.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /> {t('adminMagasins.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">{filter === 'archived' ? t('adminMagasins.emptyArchived') : t('adminMagasins.emptyActive')}</td></tr>
              ) : filtered.map((m) => (
                <tr key={m.code} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`magasin-row-${m.code}`}>
                  <td className="px-3 py-2 font-mono text-cyan-300 font-bold">
                    {m.code}
                    {m.is_default && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">{t('adminMagasins.defaultBadge')}</span>}
                  </td>
                  <td className="px-3 py-2 text-white font-medium">{m.name}</td>
                  <td className="px-3 py-2 text-gray-400 truncate max-w-[200px]">{m.address || <span className="text-gray-600">—</span>}</td>
                  <td className="px-3 py-2 text-gray-400 font-mono">{m.phone || <span className="text-gray-600">—</span>}</td>
                  <td className="px-3 py-2 text-gray-400 font-mono text-xs">
                    <div>Bixolon: {m.printer_ip || <span className="text-gray-600">—</span>}</div>
                    <div className="mt-0.5">Epson: {m.ticket_printer_ip ? `${m.ticket_printer_ip}:${m.ticket_printer_port || 9100}` : <span className="text-gray-600">—</span>}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono"
                      style={{ color: m.color || '#10b981', borderColor: `${m.color || '#10b981'}60`, backgroundColor: `${m.color || '#10b981'}15`, border: '1px solid' }}
                    >
                      <span className="w-3 h-3 rounded-full inline-block" style={{ background: m.color || '#10b981' }} />
                      {m.color || '#10b981'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {(() => {
                      const b = m.is_active ? STATUS_BADGE.active : STATUS_BADGE.archived;
                      return <span className={`px-2 py-0.5 rounded text-[11px] border ${b.cls}`}>{t(m.is_active ? 'adminMagasins.statusActive' : 'adminMagasins.statusArchived')}</span>;
                    })()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditing(m)} className="px-2 py-1 rounded bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 text-xs flex items-center gap-1" data-testid={`magasin-edit-${m.code}`}>
                        <Edit2 className="w-3 h-3" /> {t('adminMagasins.edit')}
                      </button>
                      {m.is_active ? (
                        <button onClick={() => handleArchive(m.code)} disabled={m.is_default} title={m.is_default ? t('adminMagasins.cannotArchiveDefault') : ""} className="px-2 py-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 disabled:opacity-30 disabled:cursor-not-allowed text-xs flex items-center gap-1" data-testid={`magasin-archive-${m.code}`}>
                          <Archive className="w-3 h-3" /> {t('adminMagasins.archive')}
                        </button>
                      ) : (
                        <button onClick={() => handleRestore(m.code)} className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs flex items-center gap-1" data-testid={`magasin-restore-${m.code}`}>
                          <RotateCcw className="w-3 h-3" /> {t('adminMagasins.restore')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-[11px] text-gray-500 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" />
          {t('adminMagasins.footerNote', { badge: t('adminMagasins.defaultBadge') })}
        </div>

        <CreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => fetchList()} api={api} existingMagasins={magasins} />
        <EditMagasinModal
          open={!!editing}
          magasin={editing}
          api={api}
          onClose={() => setEditing(null)}
          onSaved={() => { fetchList(); }}
        />
      </div>
      </div>
    </Layout>
  );
};

export default AdminMagasinsPage;
